import DocumentoService from '@/components/documentos/documento.service';
import VisualizadorPdfComponent from '@/components/visualizador-pdf/visualizador-pdf.vue';
import { EstadoDocumentoFormalizacion } from '@/shared/model/enumerations/estado-documentos-formalizacion.model';
import { EstadoSolicitud } from '@/shared/model/enumerations/estado-solicitud.model';
import { EstadoSolucion } from '@/shared/model/enumerations/estado-solucion.model';
import { TipoDocumentosFormalizacion } from '@/shared/model/enumerations/tipo-documentos-formalizacion';
import { TipoSolucion } from '@/shared/model/enumerations/tipo-solucion.model';
import { Filtro } from '@/shared/model/filtro.model';
import { Solicitud } from '@/shared/model/solicitud.model';
import { BvModalEvent } from 'bootstrap-vue';
import Component, { mixins } from 'vue-class-component';
import { Inject, Prop } from 'vue-property-decorator';
import GenerarPdfMixin from '../../mixins/generar-pdf-mixin';
import SolicitudMixin from '../../mixins/solicitud-mixin';

@Component({
  components: {
    'visualizador-pdf': VisualizadorPdfComponent,
  },
})
export default class FirmaIndividualFormalizacionComponent extends mixins(GenerarPdfMixin, SolicitudMixin) {
  @Inject('documentoService') private readonly documentoService: () => DocumentoService;
  @Prop({ required: true })
  public solicitudDocumentos: Solicitud;

  public documentos = null;

  public anioSolucion: number = null;

  mounted(): void {
    this.consultarDocumentos();
    this.getSolucionesVigencias();
    this.getSolucionesEvaluacion();
  }

  /**
   * Filtra soluciones por tipo MOVIMIENTOS_SITUACION y estado de la solución.
   */
  public getSolucionesVigencias(): void {
    const filtro = new Filtro();
    filtro.estadoSolucion = EstadoSolucion.PUBLICADA;
    filtro.tipoSolucion = TipoSolucion.MOVIMIENTOS_SITUACION;

    this.buscarIdSoluciones(filtro);
  }

  /**
   * Filtra soluciones por tipo y estado de la solución.
   */
  public getSolucionesEvaluacion(): void {
    const filtro = new Filtro();
    filtro.estadoSolucion = EstadoSolucion.PUBLICADA;
    filtro.tiposSolucion = [TipoSolucion.INGRESO_PERMANENCIA_PROMOCION, TipoSolucion.EMERITOS];
    filtro.solucionId = this.solicitudDocumentos?.solucionId;

    this.solucionService()
      .find(this.solicitudDocumentos.solucionId)
      .then(res => {
        this.solucion = res;
        this.anioSolucion = new Date(this.solucion.calendario.fechaInicio).getFullYear();
      })
      .catch(error => {
        this.alertService().showHttpError(this, error.response);
        throw error;
      });
  }

  /**
   * Se valida si la solicitud pertenece a una solucion de tipo movimientos vigencias.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  get isMovimientosVigencias(): boolean {
    return this.solucionesId?.some(id => id === this.solicitudDocumentos.solucionId);
  }

  /**
   * Se valida si la solicitud pertenece a una solucion de tipo movimientos vigencias y tipo cargo administrativo.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  get isMovimientoVigenciaCargoAdmvo(): boolean {
    return (
      this.solucionesId?.some(id => id === this.solicitudDocumentos.solucionId) && this.solicitudDocumentos.movimiento.tipo.clave === '24'
    );
  }

  /**
   * Se valida si la solicitud pertenece a una solucion de tipo ayudantes.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  get isAyudantes(): boolean {
    return this.solicitudDocumentos.ayudante != null;
  }

  /**
   * Determina si el texto o sección debe mostrarse para la vista previa o firmar el pdf
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  get mostrarTextoGenerarPdf(): boolean {
    if (this.isAyudantes) {
      this.tipoDocumento = TipoDocumentosFormalizacion.CONVENIO_AYUDANTES;
      return this.mostrarTextoGenerarPdfVigenciasOAyudantes();
    } else if (this.isMovimientosVigencias) {
      if (this.mostrarBotonUnaFirmaReconocimiento) {
        this.tipoDocumento = TipoDocumentosFormalizacion.RECONOCIMIENTO_VIGENCIAS;
      } else if (this.mostrarBotonUnaFirmaConvenio) {
        this.tipoDocumento = TipoDocumentosFormalizacion.CONVENIO_VIGENCIAS;
      }
      return this.mostrarTextoGenerarPdfVigenciasOAyudantes();
    } else {
      if (this.mostrarBotonUnaFirmaReconocimiento) {
        if (this.tieneUrlReconsideracion) {
          this.tipoDocumento = TipoDocumentosFormalizacion.RECONOCIMIENTO_REVISORA;
        } else {
          this.tipoDocumento = TipoDocumentosFormalizacion.RECONOCIMIENTO;
        }
      } else if (this.mostrarBotonUnaFirmaConvenio) {
        if (this.tieneUrlReconsideracion) {
          this.tipoDocumento = TipoDocumentosFormalizacion.CONVENIO_REVISORA;
        } else {
          this.tipoDocumento = TipoDocumentosFormalizacion.CONVENIO;
        }
      }

      return this.mostrarTextoGenerarPdfEvaluacion();
    }
  }

  /**
   * Determina si debe mostrarse la sección del reconocimiento.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  get mostrarSeccionReconocimiento(): boolean {
    if (this.isAyudantes || this.isMovimientosVigencias) {
      return true;
    }

    return this.mostrarBotonUrlDocumentoOficio();
  }

  /**
   * Determina si debe mostrarse la sección del convenio.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  get mostrarSeccionConvenio(): boolean {
    if (this.isAyudantes) {
      return true;
    }

    return this.mostrarBotonUrlDocumentoReconocimiento();
  }

  /**
   * Determina si el texto debe mostrarse para evaluación.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  private mostrarTextoGenerarPdfEvaluacion(): boolean {
    const estados = [EstadoSolicitud.REVISADA, EstadoSolicitud.EN_FIRMA, EstadoSolicitud.FORMALIZADA];
    return estados.includes(this.solicitudDocumentos.estado);
  }

  /**
   * Determina si el texto debe mostrarse para movimientos vigencias.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  private mostrarTextoGenerarPdfVigenciasOAyudantes(): boolean {
    const estadosVigencias = [EstadoSolicitud.APROBADA, EstadoSolicitud.PREFORMALIZADA, EstadoSolicitud.FORMALIZADA];
    const estadosAyudantes = [EstadoSolicitud.APROBADA, EstadoSolicitud.BAJA, EstadoSolicitud.FORMALIZADA];
    if (this.isAyudantes) {
      return estadosAyudantes.includes(this.solicitudDocumentos.estado);
    } else {
      return estadosVigencias.includes(this.solicitudDocumentos.estado);
    }
  }

  /**
   * Determina si se debe ocultar la sección del reconocimiento.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  get noAprobado(): boolean {
    return this.solicitudDocumentos?.vigenciasSni?.some(vigencia => {
      return vigencia.estatusVigencia.clave === 'PENDIENTE' && vigencia.nivelSni.clave === 'N';
    });
  }

  /**
   * Obtiene la URL del oficio
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  get urlOficio(): string {
    let url: string | undefined;

    if (this.tieneUrlReconsideracion) {
      url = this.anioSolucion !== 2023 ? this.solicitudDocumentos?.oficio_reconsideracion : this.solicitudDocumentos?.oficio;
    } else {
      url = this.anioSolucion !== 2023 ? this.solicitudDocumentos?.oficio_dictaminadora : this.solicitudDocumentos?.oficio;
    }

    return url || this.obtenerUrlOficio();
  }

  /**
   * Obtiene la URL del oficio en colección documentos
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  private obtenerUrlOficio() {
    const urlConvenio = this.documentos?.find(
      doc =>
        (doc.tipoDocumento === TipoDocumentosFormalizacion.OFICIO || doc.tipoDocumento === TipoDocumentosFormalizacion.OFICIO_REVISORA) &&
        doc.url &&
        doc.estado === EstadoDocumentoFormalizacion.FIRMADO
    );

    return urlConvenio.url;
  }

  /**
   * Obtiene la URL del reconocimiento
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  get urlReconocimiento(): string {
    let url: string | undefined;

    if (this.tieneUrlReconsideracion) {
      url =
        this.anioSolucion !== 2023 ? this.solicitudDocumentos?.reconocimiento_reconsideracion : this.solicitudDocumentos?.reconocimiento;
    } else {
      if (this.isMovimientosVigencias) {
        url = this.solicitudDocumentos?.reconocimiento;
      } else if (this.anioSolucion !== 2023) {
        url = this.solicitudDocumentos?.reconocimiento_dictaminadora;
      } else {
        url = this.solicitudDocumentos?.reconocimiento;
      }
    }

    return url || this.obtenerUrlReconocimiento();
  }

  /**
   * Obtiene la URL del reconocimiento en colección documentos
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  private obtenerUrlReconocimiento() {
    const urlReconocimiento = this.documentos?.find(
      doc =>
        (doc.tipoDocumento === TipoDocumentosFormalizacion.RECONOCIMIENTO ||
          doc.tipoDocumento === TipoDocumentosFormalizacion.RECONOCIMIENTO_REVISORA ||
          doc.tipoDocumento === TipoDocumentosFormalizacion.RECONOCIMIENTO_VIGENCIAS) &&
        doc.url &&
        doc.estado === EstadoDocumentoFormalizacion.FIRMADO
    );

    return urlReconocimiento.url;
  }

  /**
   * Obtiene la URL del convenio
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  get urlconvenio(): string {
    let url: string | undefined;

    if (this.tieneUrlReconsideracion) {
      url = this.anioSolucion !== 2023 ? this.solicitudDocumentos?.urlConvenio_reconsideracion : this.solicitudDocumentos?.urlConvenio;
    } else {
      if (this.isMovimientosVigencias || this.isAyudantes) {
        url = this.solicitudDocumentos?.urlConvenio;
      } else if (this.anioSolucion !== 2023) {
        url = this.solicitudDocumentos?.urlConvenio_dictaminadora;
      } else {
        url = this.solicitudDocumentos?.urlConvenio;
      }
    }

    return url || this.obtenerUrlConvenio();
  }

  /**
   * Obtiene la URL del convenio en colección documentos
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  private obtenerUrlConvenio() {
    const urlConvenio = this.documentos?.find(
      doc =>
        (doc.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO ||
          doc.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_REVISORA ||
          doc.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_AYUDANTES ||
          doc.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_VIGENCIAS) &&
        doc.url &&
        doc.estado === EstadoDocumentoFormalizacion.FIRMADO
    );

    return urlConvenio.url;
  }

  /**
   * Obtiene la URL de reconsideración
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  get tieneUrlReconsideracion(): boolean {
    return !!this.solicitudDocumentos?.reconsideracion?.uri;
  }

  /**
   * Determina si se debe mostrar el botón para visualizar el oficio mediante la URL.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  public mostrarBotonUrlDocumentoOficio(): boolean {
    if (this.tieneUrlReconsideracion) {
      const existeUrl =
        this.anioSolucion !== 2023 ? !!this.solicitudDocumentos?.oficio_reconsideracion : !!this.solicitudDocumentos?.oficio;
      return existeUrl || this.existeUrlOficio();
    } else {
      const existeUrl = this.anioSolucion !== 2023 ? !!this.solicitudDocumentos?.oficio_dictaminadora : !!this.solicitudDocumentos?.oficio;
      return existeUrl || this.existeUrlOficio();
    }
  }

  /**
   * Determina si existe la url del oficio en la colección documentos.
   *
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  public existeUrlOficio(): boolean {
    return this.documentos?.some(
      doc =>
        doc.url &&
        (doc.tipoDocumento === TipoDocumentosFormalizacion.OFICIO || doc.tipoDocumento === TipoDocumentosFormalizacion.OFICIO_REVISORA) &&
        doc.estado === EstadoDocumentoFormalizacion.FIRMADO
    );
  }

  /**
   * Determina si se debe mostrar el botón para visualizar el reconocimiento mediante la URL.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  public mostrarBotonUrlDocumentoReconocimiento(): boolean {
    if (this.tieneUrlReconsideracion) {
      const existeUrl =
        this.anioSolucion !== 2023
          ? !!this.solicitudDocumentos?.reconocimiento_reconsideracion
          : !!this.solicitudDocumentos?.reconocimiento;
      return existeUrl || this.existeUrlReconocimiento();
    } else {
      let existeUrl: boolean;
      if (this.isMovimientosVigencias) {
        existeUrl = !!this.solicitudDocumentos?.reconocimiento;
      } else if (this.anioSolucion !== 2023) {
        existeUrl = !!this.solicitudDocumentos?.reconocimiento_dictaminadora;
      } else {
        existeUrl = !!this.solicitudDocumentos?.reconocimiento;
      }

      return existeUrl || this.existeUrlReconocimiento();
    }
  }

  /**
   * Determina si existe la url del reconocimiento en la colección documentos.
   *
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  public existeUrlReconocimiento(): boolean {
    return this.documentos?.some(
      doc =>
        (doc.tipoDocumento === TipoDocumentosFormalizacion.RECONOCIMIENTO ||
          doc.tipoDocumento === TipoDocumentosFormalizacion.RECONOCIMIENTO_REVISORA ||
          doc.tipoDocumento === TipoDocumentosFormalizacion.RECONOCIMIENTO_VIGENCIAS) &&
        doc.url &&
        doc.estado === EstadoDocumentoFormalizacion.FIRMADO
    );
  }

  /**
   * Determina si se debe mostrar el botón para visualizar el reconocimiento mediante la URL.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  get mostrarBotonUrlDocumentoConvenio(): boolean {
    if (this.tieneUrlReconsideracion) {
      const existeUrl =
        this.anioSolucion !== 2023 ? !!this.solicitudDocumentos?.urlConvenio_reconsideracion : !!this.solicitudDocumentos?.urlConvenio;
      return existeUrl || this.existeUrlConvenio();
    } else {
      const existeUrl =
        this.isAyudantes || this.isMovimientosVigencias
          ? !!this.solicitudDocumentos?.urlConvenio
          : this.anioSolucion !== 2023
          ? !!this.solicitudDocumentos?.urlConvenio_dictaminadora
          : !!this.solicitudDocumentos?.urlConvenio;

      return existeUrl || this.existeUrlConvenio();
    }
  }

  /**
   * Determina si existe la url del convenio en la colección documentos.
   *
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  public existeUrlConvenio(): boolean {
    return this.documentos?.some(
      doc =>
        (doc.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO ||
          doc.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_REVISORA ||
          doc.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_AYUDANTES ||
          doc.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_VIGENCIAS) &&
        doc.url &&
        doc.estado === EstadoDocumentoFormalizacion.FIRMADO
    );
  }

  /**
   * Determina si se debe mostrar el botón para visualizar el documento con la firma de la secretaría.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  get mostrarBotonUnaFirmaReconocimiento(): boolean {
    const isSolicitante = this.solicitudDocumentos?.solicitante?.cvu == this.cvu;
    const firmadoSecihti = this.documentos?.some(documento => {
      return (
        (documento.tipoDocumento === TipoDocumentosFormalizacion.RECONOCIMIENTO ||
          documento.tipoDocumento === TipoDocumentosFormalizacion.RECONOCIMIENTO_REVISORA ||
          documento.tipoDocumento === TipoDocumentosFormalizacion.RECONOCIMIENTO_VIGENCIAS) &&
        documento.estado === EstadoDocumentoFormalizacion.FIRMADO_POR_SECIHTI
      );
    });

    if (firmadoSecihti && isSolicitante) {
      return true;
    }

    return false;
  }

  /**
   * Determina si se debe mostrar el botón para visualizar el documento con la firma de la secretaría.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  get mostrarBotonUnaFirmaConvenio(): boolean {
    let firmadoSecihti;
    const esSolicitante = this.solicitudDocumentos?.solicitante?.cvu === this.cvu;
    const esAyudante = this.solicitudDocumentos?.ayudante?.cvu === this.cvu;

    if (this.isAyudantes) {
      if (esSolicitante && this.esBajaPorInvestigador) {
        firmadoSecihti = this.documentos?.some(documento => {
          return (
            documento.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_AYUDANTES &&
            documento.estado === EstadoDocumentoFormalizacion.FIRMADO_POR_SECIHTI
          );
        });
      } else if (esAyudante && this.esBajaPorAyudante) {
        firmadoSecihti = this.documentos?.some(documento => {
          return (
            documento.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_AYUDANTES &&
            documento.estado === EstadoDocumentoFormalizacion.FIRMADO_POR_SECIHTI
          );
        });
      } else {
        firmadoSecihti = this.documentos?.some(documento => {
          return (
            documento.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_AYUDANTES &&
            documento.estado === EstadoDocumentoFormalizacion.FIRMADO_POR_SECIHTI &&
            esSolicitante &&
            !this.esBajaPorInvestigador &&
            !this.esBajaPorAyudante
          );
        });
      }
    } else {
      firmadoSecihti = this.documentos?.some(documento => {
        return (
          (documento.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO ||
            documento.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_REVISORA ||
            documento.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_AYUDANTES ||
            documento.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_VIGENCIAS) &&
          documento.estado === EstadoDocumentoFormalizacion.FIRMADO_POR_SECIHTI
        );
      });
    }

    return firmadoSecihti;
  }

  get esBajaPorInvestigador() {
    return this.solicitudDocumentos?.tipoMovimiento === 'Baja ayudantes por investigador';
  }

  get esBajaPorAyudante() {
    return this.solicitudDocumentos?.tipoMovimiento === 'Baja ayudantes por ayudante';
  }

  get esAltaAyudante() {
    return this.solicitudDocumentos?.tipoMovimiento === 'Alta ayudantes';
  }

  /**
   * Obtiene el estado del pdf tipo convenio
   * @param solicitud Solicitud que contiene los datos necesarios.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  public obtenerEstadoConvenio(): string {
    if (this.tieneUrlReconsideracion) {
      const url =
        this.anioSolucion !== 2023 ? this.solicitudDocumentos?.urlConvenio_reconsideracion : this.solicitudDocumentos?.urlConvenio;

      return url ? EstadoDocumentoFormalizacion.FIRMADO : this.estadoConvenio();
    } else {
      let url: string | undefined;

      if (this.isAyudantes || this.isMovimientosVigencias) {
        url = this.solicitudDocumentos?.urlConvenio;
      } else if (this.anioSolucion !== 2023) {
        url = this.solicitudDocumentos?.urlConvenio_dictaminadora;
      } else {
        url = this.solicitudDocumentos?.urlConvenio;
      }

      return url ? EstadoDocumentoFormalizacion.FIRMADO : this.estadoConvenio();
    }
  }

  /**
   * Obtiene el estado de un documento
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  private estadoConvenio() {
    const documentosConvenio = this.documentos?.filter(
      doc =>
        doc.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO ||
        doc.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_REVISORA ||
        doc.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_AYUDANTES ||
        doc.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_VIGENCIAS
    );

    if (documentosConvenio?.length === 0) {
      return 'firma por SECIHTI';
    }

    const estados = documentosConvenio?.map(doc => doc.estado);
    if (this.isAyudantes) {
      if (this.esBajaPorAyudante) {
        if (estados?.includes(EstadoDocumentoFormalizacion.FIRMADO_POR_SECIHTI)) {
          return 'firma por el ayudante';
        }
      } else if (this.esBajaPorInvestigador) {
        if (estados?.includes(EstadoDocumentoFormalizacion.FIRMADO_POR_SECIHTI)) {
          return 'firma por el investigador';
        }
      } else {
        if (estados?.includes(EstadoDocumentoFormalizacion.FIRMADO_POR_SECIHTI)) {
          return 'firma por el investigador';
        }

        if (estados?.includes(EstadoDocumentoFormalizacion.FIRMADO_POR_INVESTIGADOR)) {
          return 'firma por el ayudante';
        }
      }
    } else {
      if (estados?.includes(EstadoDocumentoFormalizacion.FIRMADO_POR_SECIHTI)) {
        return 'firma por SECIHTI';
      }
    }
  }

  get cvu(): string {
    return this.$store.getters.account?.id ?? '';
  }

  /**
   * Obtiene el estado del pdf tipo reconocimiento
   * @param solicitud Solicitud que contiene los datos necesarios.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  public obtenerEstadoReconocimiento(): string {
    if (this.tieneUrlReconsideracion) {
      const url =
        this.anioSolucion !== 2023 ? this.solicitudDocumentos?.reconocimiento_reconsideracion : this.solicitudDocumentos?.reconocimiento;

      return url ? EstadoDocumentoFormalizacion.FIRMADO : this.estadoReconocimiento();
    } else {
      let url: string | undefined;

      if (this.isMovimientosVigencias) {
        url = this.solicitudDocumentos?.reconocimiento;
      } else if (this.anioSolucion !== 2023) {
        url = this.solicitudDocumentos?.reconocimiento_dictaminadora;
      } else {
        url = this.solicitudDocumentos?.reconocimiento;
      }

      return url ? EstadoDocumentoFormalizacion.FIRMADO : this.estadoReconocimiento();
    }
  }

  /**
   * Obtiene el estado de un documento tipo reconocimiento
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  private estadoReconocimiento() {
    const documentosReconocimiento = this.documentos?.filter(
      doc =>
        doc.tipoDocumento === TipoDocumentosFormalizacion.RECONOCIMIENTO ||
        doc.tipoDocumento === TipoDocumentosFormalizacion.RECONOCIMIENTO_REVISORA ||
        doc.tipoDocumento === TipoDocumentosFormalizacion.RECONOCIMIENTO_VIGENCIAS
    );

    if (documentosReconocimiento?.length === 0) {
      return 'firma por SECIHTI';
    }

    const estados = documentosReconocimiento?.map(doc => doc.estado);

    if (estados?.includes(EstadoDocumentoFormalizacion.CANCELADO)) {
      if (estados?.includes(EstadoDocumentoFormalizacion.FIRMADO_POR_SECIHTI)) {
        return 'firma por el investigador';
      }

      return 'firma por SECIHTI';
    }

    if (estados?.includes(EstadoDocumentoFormalizacion.FIRMADO_POR_SECIHTI)) {
      return 'firma por el investigador';
    }
  }

  /**
   * Determina si se debe mostrar el botón para visualizar el documento con la firma de la secretaría.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  get mostrarBotonFirmaSolicitante(): boolean {
    if (this.isAyudantes) {
      const isSolicitante = this.solicitudDocumentos.solicitante.cvu == this.cvu;
      const firmadoPorSolicitante = this.documentos?.some(documento => {
        return (
          documento.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_AYUDANTES &&
          documento.estado === EstadoDocumentoFormalizacion.FIRMADO_POR_INVESTIGADOR
        );
      });

      return firmadoPorSolicitante && isSolicitante;
    }

    return false;
  }

  /**
   * Determina si se debe mostrar el botón para visualizar el documento con la firma del investigador.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  get mostrarBotonDosFirmasConvenio(): boolean {
    if (this.isAyudantes) {
      const isAyudante = this.solicitudDocumentos.ayudante.cvu == this.cvu;
      const firmadoPorSolicitante = this.documentos?.some(documento => {
        return (
          documento.tipoDocumento === TipoDocumentosFormalizacion.CONVENIO_AYUDANTES &&
          documento.estado === EstadoDocumentoFormalizacion.FIRMADO_POR_INVESTIGADOR
        );
      });

      return firmadoPorSolicitante && isAyudante;
    }

    return false;
  }

  /**
   * Método para generar un documento de una solicitud en formato PDF.
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  public async generarDocumento(bvModalEvent: BvModalEvent) {
    this.show = true;
    this.pdfFirma = true;
    try {
      this.generarFirma(
        bvModalEvent,
        this.generarPdfService().exportarPdfFormalizacion,
        this.solicitudDocumentos.id,
        this.password,
        this.tipoDocumento,
        async solicitudActualizada => {
          try {
            this.documentos.push(solicitudActualizada);
            if (this.isAyudantes) {
              await this.actualizarSolicitudAyudantes(this.solicitudDocumentos);
            } else {
              const puedeFormalizar = this.mostrarBotonUrlDocumentoConvenio;
              await this.actualizarSolicitudMovimientosVigencias(this.solicitudDocumentos, puedeFormalizar);
            }

            await this.consultarDocumentos();
          } catch (error) {
            this.alertService().showError(this, this.$t(error.response?.data?.detail || 'Error al generar documento', 'Error').toString());
          } finally {
            this.pdfFirma = false;
            this.show = false;
          }
        },
        error => {
          this.pdfFirma = false;
          this.show = false;
          this.alertService().showError(this, this.$t(error.response?.data?.detail || 'Error al generar documento', 'Error').toString());
        }
      );
    } catch (error) {
      this.pdfFirma = false;
      this.show = false;
      this.alertService().showError(this, this.$t(error.response?.data?.detail || 'Error al generar documento', 'Error').toString());
    }
  }

  /**
   * Metodo para actualizar la solicitud de tipo ayudantes
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  public async actualizarSolicitudAyudantes(solicitud: Solicitud) {
    const esAlta = solicitud?.tipoMovimiento === 'Alta ayudantes';
    const esBajaPorInvestigador = solicitud?.tipoMovimiento === 'Baja ayudantes por investigador';
    const esBajaPorAyudante = solicitud?.tipoMovimiento === 'Baja ayudantes por ayudante';

    const esSolicitante = solicitud?.solicitante?.cvu === this.cvu;
    const esAyudante = solicitud?.ayudante?.cvu === this.cvu;

    const puedeFormalizar = this.isAyudantes && esAlta && esAyudante;
    const puedeDarBaja = this.isAyudantes && ((esBajaPorInvestigador && esSolicitante) || (esBajaPorAyudante && esAyudante) || esAyudante);

    if (puedeFormalizar) {
      solicitud.estado = EstadoSolicitud.FORMALIZADA;
      await this.formalizarSolicitud(solicitud);
    } else if (puedeDarBaja) {
      solicitud.estado = EstadoSolicitud.BAJA;
      await this.bajaSolicitud(solicitud);
    }
  }

  /**
   * Metodo para actualizar la solicitud de tipo movimientos vigencias
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  public async actualizarSolicitudMovimientosVigencias(solicitud: Solicitud, puedeFormalizar: boolean) {
    if (puedeFormalizar) {
      await this.formalizarSolicitud(solicitud);
      solicitud.estado = EstadoSolicitud.FORMALIZADA;
    } else if (this.isMovimientosVigencias && !puedeFormalizar) {
      await this.preFormalizarSolicitud(solicitud);

      solicitud.estado = EstadoSolicitud.PREFORMALIZADA;
    } else {
      await this.enviaFirma(solicitud);
    }
  }

  /**
   * Metodo para consultar los documentos de la solicitud
   *
   * @memberof FirmaIndividualFormalizacionComponent
   */
  public async consultarDocumentos(): Promise<void> {
    try {
      const res = await this.documentoService().findByDocumentosFormalizacionSolicitudId(this.solicitudDocumentos.id);

      this.documentos = res;
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
      throw error;
    }
  }
}
